import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import './HeroCarousel.css';

const HeroCarousel = ({ slides }) => {
  const { t } = useTranslation('common');
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const videoRefs = useRef([]);
  const autoPlayRef = useRef(null);

  // Default slides if none provided
  const defaultSlides = [
    {
      id: 1,
      video: '/videos/slide-1.mp4',
      label: 'INFININOVA LIMITED COMPANY',
      title: 'Navigate Market',
      titleHighlight: 'Complexity',
      description: 'Launch and grow your brand across global markets with in-market teams, proprietary AI intelligence, and end-to-end execution.',
      tabLabel: 'Growth',
      link: '/services'
    },
    {
      id: 2,
      video: '/videos/slide-2.mp4',
      label: 'INSIGHTS',
      title: 'AI-Enabled Analytics',
      titleHighlight: 'Platform',
      description: 'Our Commerce Intelligence brings together platform data, search behavior, product trends, and competitor signals for real-time market insights.',
      tabLabel: 'Intelligence',
      link: '/services#intelligence'
    },
    {
      id: 3,
      video: '/videos/slide-3.mp4',
      label: 'OPERATIONS',
      title: 'Global Logistics &',
      titleHighlight: 'Fulfillment',
      description: 'Expanded warehousing and fulfillment capacity strengthens logistics, shortens delivery times, and increases operational control.',
      tabLabel: 'Logistics',
      link: '/services#operations'
    }
  ];

  const activeSlides = slides || defaultSlides;

  // Initial video load on mount
  useEffect(() => {
    // Try to play the first video when component mounts
    setTimeout(() => {
      if (videoRefs.current[0]) {
        videoRefs.current[0].load();
        videoRefs.current[0].play().catch(e => {
          console.log('Initial video autoplay prevented:', e);
        });
      }
    }, 500);
  }, []);

  // Handle video playback when slide changes
  useEffect(() => {
    const playCurrentVideo = () => {
      videoRefs.current.forEach((video, index) => {
        if (video) {
          if (index === currentSlide) {
            // Ensure video is ready before playing
            video.load(); // Reload the video
            const playPromise = video.play();
            if (playPromise !== undefined) {
              playPromise
                .then(() => {
                  console.log('Video playing successfully');
                })
                .catch(error => {
                  console.log('Video autoplay prevented:', error);
                  // Try to play again after a small delay
                  setTimeout(() => {
                    video.play().catch(e => console.log('Retry failed:', e));
                  }, 100);
                });
            }
          } else {
            video.pause();
            video.currentTime = 0;
          }
        }
      });
    };

    playCurrentVideo();
  }, [currentSlide]);

  // Auto-advance carousel
  useEffect(() => {
    const goToNext = () => {
      setCurrentSlide((prev) => (prev === activeSlides.length - 1 ? 0 : prev + 1));
    };

    autoPlayRef.current = setInterval(goToNext, 8000);

    return () => {
      if (autoPlayRef.current) {
        clearInterval(autoPlayRef.current);
      }
    };
  }, [activeSlides.length]);

  const goToSlide = (index) => {
    if (isTransitioning || index === currentSlide) return;
    setIsTransitioning(true);
    setCurrentSlide(index);
    
    // Reset autoplay timer
    if (autoPlayRef.current) {
      clearInterval(autoPlayRef.current);
    }
    
    setTimeout(() => {
      setIsTransitioning(false);
    }, 600);
  };

  const goToPrev = () => {
    const newIndex = currentSlide === 0 ? activeSlides.length - 1 : currentSlide - 1;
    goToSlide(newIndex);
  };

  const goToNext = () => {
    const newIndex = currentSlide === activeSlides.length - 1 ? 0 : currentSlide + 1;
    goToSlide(newIndex);
  };

  return (
    <section className="hero-carousel">
      {/* Video Backgrounds */}
      <div className="carousel-backgrounds">
        {activeSlides.map((slide, index) => (
          <div
            key={slide.id}
            className={`carousel-bg ${index === currentSlide ? 'active' : ''}`}
          >
            <video
              ref={(el) => (videoRefs.current[index] = el)}
              className="carousel-video"
              muted
              loop
              playsInline
              preload="metadata"
              onLoadedData={() => console.log(`Video ${index} loaded`)}
              onError={(e) => console.error(`Video ${index} error:`, e)}
            >
              <source src={slide.video} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
            <div className="carousel-overlay"></div>
          </div>
        ))}
      </div>

      {/* Slide Content */}
      <div className="carousel-content">
        <div className="container">
          {activeSlides.map((slide, index) => (
            <div
              key={slide.id}
              className={`slide-content ${index === currentSlide ? 'active' : ''}`}
            >
              <p className="slide-label">{slide.label}</p>
              <h1 className="slide-title">
                {slide.title}
                {slide.titleHighlight && (
                  <>
                    <br />
                    <span className="title-highlight">{slide.titleHighlight}</span>
                  </>
                )}
              </h1>
              <p className="slide-description">{slide.description}</p>
              {slide.link && (
                <Link to={slide.link} className="slide-link">
                  {t('home.hero.learnMore')}
                </Link>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Tab Navigation (Bottom) */}
      <div className="carousel-tabs">
        <div className="container">
          <div className="tabs-wrapper">
            {activeSlides.map((slide, index) => (
              <button
                key={slide.id}
                className={`carousel-tab ${index === currentSlide ? 'active' : ''}`}
                onClick={() => goToSlide(index)}
              >
                {slide.tabLabel}
                <span className="tab-progress">
                  <span 
                    className="tab-progress-bar"
                    style={{
                      animationDuration: index === currentSlide ? '8s' : '0s',
                      animationPlayState: index === currentSlide ? 'running' : 'paused'
                    }}
                  ></span>
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Arrow Navigation */}
      <button 
        className="carousel-arrow carousel-arrow-left" 
        onClick={goToPrev}
        aria-label="Previous slide"
      >
        <FiChevronLeft />
      </button>
      <button 
        className="carousel-arrow carousel-arrow-right" 
        onClick={goToNext}
        aria-label="Next slide"
      >
        <FiChevronRight />
      </button>
    </section>
  );
};

export default HeroCarousel;
